import { createActions, handleActions } from 'redux-actions';
import Notifications from 'react-notification-system-redux';
import {
    defaultPaginationData,
    getPaginationAndOrderData,
} from '../../shared/utils';

const actions = createActions({
    PLASMASCREENS: {
        LIST: {
            FETCHING: currentPage => ({ currentPage }),
            LOADED: list => ({ list }),
        },
        ADD: {
            ENTERED_REFERENCE_CHANGE: enteredReference => ({
                enteredReference,
            }),
            PROMPT: undefined,
            ADDING: undefined,
            ADDED: undefined,
            RESET: undefined,
        },
        REMOVE: {
            PROMPT: ({ plasmascreenId, plasmascreenPropertyName }) => ({
                plasmascreenId,
                plasmascreenPropertyName,
            }),
            REMOVING: undefined,
            REMOVED: undefined,
            RESET: undefined,
        },
        REORDER: {
            REORDERING: undefined,
            REORDERED: undefined,
        },
    },
});

const initialState = Object.assign(
    {},
    {
        list: Object.assign(
            {},
            {
                error: null,
                fetching: false,
                fetchingTime: null,
                plasmascreens: null,
                currentPage: 1,
            },
            defaultPaginationData
        ),
        add: {
            enteredReference: null,
            error: null,
            adding: false,
            visible: false,
        },
        remove: {
            plasmascreenId: null,
            propertyName: null,
            removing: false,
        },
        reorder: {
            reordering: false,
            reordered: false,
        },
    }
);

let fetchingTimer;

export default handleActions(
    {
        [actions.plasmascreens.list.fetching]: (state, action) => {
            fetchingTimer = Date.now();

            return Object.assign({}, state, {
                list: Object.assign({}, state.list, {
                    fetching: true,
                    currentPage: action.payload.currentPage,
                }),
            });
        },

        [actions.plasmascreens.list.loaded]: {
            next: (state, action) => {
                fetchingTimer = Date.now() - fetchingTimer;

                return Object.assign({}, state, {
                    list: Object.assign(
                        {},
                        state.list,
                        getPaginationAndOrderData(action.payload.list.meta),
                        {
                            plasmascreens: action.payload.list.results.reduce(
                                (result, plasmascreen) => {
                                    result[
                                        plasmascreen.propertyPlasmaScreenId
                                    ] = {
                                        plasmascreenId:
                                            plasmascreen.propertyPlasmaScreenId,
                                        plasmascreenPropertyId:
                                            plasmascreen.property.propertyId,
                                        plasmascreenPropertyName:
                                            plasmascreen.property.propertyName,
                                        plasmascreenPropertyPostCode:
                                            plasmascreen.property
                                                .propertyPostCode,
                                        plasmascreenPropertyReference:
                                            plasmascreen.property
                                                .propertyReference,
                                        plasmascreenPropertyStatus:
                                            plasmascreen.property
                                                .propertyStatus,
                                    };
                                    return result;
                                },
                                {}
                            ),
                            fetching: false,
                            fetchingTime: fetchingTimer,
                        }
                    ),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    list: Object.assign({}, initialState.list, {
                        error: true,
                    }),
                });
            },
        },

        [actions.plasmascreens.add.prompt]: (state, action) => {
            return Object.assign({}, state, {
                add: Object.assign({}, initialState.add, {
                    visible: true,
                }),
            });
        },

        [actions.plasmascreens.add.reset]: (state, action) => {
            return Object.assign({}, state, {
                add: Object.assign({}, initialState.add),
            });
        },

        [actions.plasmascreens.add.enteredReferenceChange]: (state, action) => {
            const { enteredReference } = action.payload;

            return Object.assign({}, state, {
                add: Object.assign({}, state.add, {
                    enteredReference: enteredReference.toUpperCase(),
                }),
            });
        },

        [actions.plasmascreens.add.adding]: (state, action) => {
            return Object.assign({}, state, {
                add: Object.assign({}, initialState.add, {
                    adding: true,
                }),
            });
        },

        [actions.plasmascreens.add.added]: (state, action) => {
            return Object.assign({}, state, {
                add: Object.assign({}, initialState.add),
            });
        },

        [actions.plasmascreens.remove.prompt]: (state, action) => {
            const { plasmascreenId, plasmascreenPropertyName } = action.payload;

            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove, {
                    plasmascreenId,
                    plasmascreenPropertyName,
                }),
            });
        },

        [actions.plasmascreens.remove.removing]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, state.remove, {
                    removing: true,
                }),
            });
        },

        [actions.plasmascreens.remove.removed]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove),
            });
        },

        [actions.plasmascreens.remove.reset]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove),
            });
        },

        [actions.plasmascreens.reorder.reordering]: (state, action) => {
            return Object.assign({}, state, {
                reorder: Object.assign({}, state.reorder, {
                    reordering: true,
                }),
            });
        },

        [actions.plasmascreens.reorder.reordered]: (state, action) => {
            return Object.assign({}, state, {
                reorder: Object.assign({}, initialState.reorder),
            });
        },
    },
    initialState
);

export const loadPlasmaScreensProperties = (currentPage, limit = 25) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.plasmascreens.list.fetching({ currentPage }));

        return api
            .get(`/propertyplasmascreens?limit=${limit}&page=${currentPage}`)
            .then(
                response =>
                    dispatch(actions.plasmascreens.list.loaded(response.data)),
                err => dispatch(actions.plasmascreens.list.loaded(err))
            );
    };
};

export const showAddPropertyToPlasmaScreenPrompt = (
    propertyReference: string
) => {
    return actions.plasmascreens.add.prompt({ propertyReference });
};

export const addPromptReset = () => {
    return actions.plasmascreens.add.reset();
};

export const addPromptEnteredReferenceChange = (value: string) => {
    return actions.plasmascreens.add.enteredReferenceChange(value);
};

export const addPropertyToPlasmaScreens = propertyReference => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.plasmascreens.add.adding());

        return api
            .post(`/propertyplasmascreens`, {
                propertyReference: propertyReference,
            })
            .then(
                response => {
                    dispatch(actions.plasmascreens.add.added(response.data));

                    dispatch(
                        Notifications.success({
                            title: 'Property added',
                            message: 'The provided details have been saved.',
                            position: 'tc',
                            autoDismiss: 3,
                        })
                    );
                },
                err => {
                    dispatch(actions.plasmascreens.add.added(err));
                    dispatch(
                        Notifications.error({
                            title: 'Addition failed',
                            message:
                                'An error occurred whilst adding this property to plasma screens. Please try again.',
                            position: 'tc',
                            autoDismiss: 5,
                        })
                    );
                    throw err;
                }
            );
    };
};

export const showDeletePropertyFromPlasmaScreenPrompt = (
    plasmascreenId: string,
    plasmascreenPropertyName: string
) => {
    return actions.plasmascreens.remove.prompt({
        plasmascreenId,
        plasmascreenPropertyName,
    });
};

export const deletePromptReset = () => {
    return actions.plasmascreens.remove.reset();
};

export const removePropertyFromPlasmaScreens = (
    plasmascreenId: string,
    plasmascreenPropertyName: string
) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.plasmascreens.remove.removing());

        return api.delete(`/propertyplasmascreens/${plasmascreenId}`).then(
            response => {
                dispatch(actions.plasmascreens.remove.removed());

                dispatch(
                    Notifications.success({
                        title: 'Property deleted',
                        message: `The property ${plasmascreenPropertyName} has been deleted.`,
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );
            },
            err => {
                dispatch(actions.plasmascreens.remove.removed(err));
                dispatch(
                    Notifications.error({
                        title: 'Delete failed',
                        message:
                            'An error occurred whilst removing this property from plasma screens. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};

export const reorderPropertiesOnPlasmaScreen = () => {
    return actions.plasmascreens.reorder.reordering();
};

export const updatePropertyPlasmaScreenOrder = (plasmascreens: Array) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.plasmascreens.reorder.reordering());

        const plasmaScreensArray = Array.isArray(plasmascreens)
            ? plasmascreens.map((plasmascreen, index) => {
                  return {
                      plasmascreenId: plasmascreen.plasmascreenId,
                      plasmascreenPropertyId:
                          plasmascreen.plasmascreenPropertyId,
                      order: index + 1,
                  };
              })
            : [];

        return api
            .put(`/propertyplasmascreens`, {
                propertyPlasmaScreens: plasmaScreensArray,
            })
            .then(
                response => {
                    dispatch(actions.plasmascreens.reorder.reordered());

                    dispatch(
                        Notifications.success({
                            title: 'Order saved',
                            message: `The plasma screen list order has been saved.`,
                            position: 'tc',
                            autoDismiss: 3,
                        })
                    );
                },
                err => {
                    dispatch(actions.plasmascreens.reorder.reordered(err));
                    dispatch(
                        Notifications.error({
                            title: 'Reorder failed',
                            message:
                                'An error occurred whilst saving the plasma screen order. Please try again.',
                            position: 'tc',
                            autoDismiss: 5,
                        })
                    );
                    throw err;
                }
            );
    };
};



// WEBPACK FOOTER //
// ./src/redux/reducers/plasmascreens.js